import React, { Fragment, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DesktopDateTimePicker, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const DateRange = ({
  id,
  label,
  dateNeeded,
  allDay,
  setDateNeeded,
  error,
  setError,
  errorKey,
  readOnly
}) => {
  const [openDateTime, setOpenDateTime] = useState(false);
  const [openDate, setOpenDate] = useState(false);

  const theme = createTheme({
    components: {
      MuiMultiSectionDigitalClock: {
        styleOverrides: {
          root: {
            "& .MuiList-root": {
              "&::-webkit-scrollbar": {
                display: "none"
              },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none",
              border: "none",
              height: "225px",
              marginTop: "5px",
              maxWidth: "40px",
              "& .MuiMultiSectionDigitalClockSection-item": {
                maxWidth: "30px",
                maxHeight: "30px",
                fontSize: "14px"
              },
              "&:after": {
                height: "calc(100% - 30px - 4px)"
              }
            }
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            display: "none"
          }
        }
      },
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            height: "225px",
            maxWidth: "250px"
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            height: "36px",
            width: "255px",
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            justifyContent: "space-between",
            "& .MuiButton-text": {
              fontSize: "11px"
            }
          }
        }
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            width: "30px",
            height: "30px"
          }
        }
      },
      MuiDayCalendar: {
        styleOverrides: {
          root: {
            "& .MuiDayCalendar-header": {
              justifyContent: "flex-start",
              marginLeft: "5px"
            },
            "& .MuiDayCalendar-weekDayLabel": {
              width: "30px",
              height: "30px",
              fontWeight: "bold"
            }
          }
        }
      },
      MuiPickersSlideTransition: {
        styleOverrides: {
          root: {
            "& .MuiDayCalendar-weekContainer": {
              justifyContent: "flex-start",
              marginLeft: "5px"
            }
          }
        }
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            paddingLeft: "10px",
            paddingRight: 0,
            margin: 0,
            marginTop: "5px"
          }
        }
      },
      MuiPickersYear: {
        styleOverrides: {
          root: {
            maxWidth: "50px"
          }
        }
      },
      MuiYearCalendar: {
        styleOverrides: {
          root: {
            maxWidth: "240px",
            justifyContent: "center"
          }
        }
      },
      MuiMonthCalendar: {
        styleOverrides: {
          root: {
            maxWidth: "240px",
            justifyContent: "center"
          }
        }
      },
      MuiPickersMonth: {
        styleOverrides: {
          root: {
            maxWidth: "50px"
          }
        }
      },
      MuiPickersFadeTransitionGroup: {
        styleOverrides: {
          root: {
            "& .MuiPickersCalendarHeader-label": {
              fontSize: "15.5px"
            }
          }
        }
      }
    }
  });

  // Handle date change
  const handleDateChange = (newValue) => {
    setDateNeeded(newValue);
    setError((prevError) => ({
      ...prevError,
      [errorKey]: null 
    }));
    if (!readOnly) {
      setError((prevError) => ({
        ...prevError,
        [errorKey]: null 
      }));
    }
  };

  // Error check helper
  const isError = Boolean(error && error[errorKey]);

  return (
    <Fragment>
      {allDay === "no" ? (
        <ThemeProvider theme={theme}>
          <DesktopDateTimePicker
            views={["year", "month", "day", "hours", "minutes"]}
            label={label || ""}
            id={id || "date_needed_input"}
            value={dateNeeded}
            minDate={dayjs("2010-01-01")}
            maxDate={dayjs().add(5, "years")}
            onChange={handleDateChange}
            onOpen={() => setOpenDateTime(true)}
            onClose={() => setOpenDateTime(false)}
            open={openDateTime}
            slotProps={{
              textField: {
                error: isError,
                helperText: isError ? error[errorKey] : "",
                fullWidth: true,
                size: "small",
                inputProps: {
                  style: { fontSize: 14 },
                  onClick: () => setOpenDateTime(true),
                  readOnly: readOnly
                },
                placeholder: "",
                sx: {
                  '& .MuiOutlinedInput-input': {
                    backgroundColor: 'white !important'
                },
                }
              },
              actionBar: {
                actions: ["clear", "today"]
              }
            }}
              readOnly={readOnly}
          />
        </ThemeProvider>
      ) : (
        <DatePicker
          views={["year", "month", "day"]}
          label={label || ""}
          id="date_needed_input"
          minDate={dayjs("1924-01-01")}
          maxDate={dayjs().add(5, "years")}
          value={dateNeeded}
          onChange={handleDateChange}
          onOpen={() => setOpenDate(true)}
          onClose={() => setOpenDate(false)}
          open={openDate}
          slotProps={{
            textField: {
              error: isError,
              helperText: isError ? error[errorKey] : "",
              fullWidth: true,
              size: "small",
              inputProps: {
                style: { fontSize: 14 },
                onClick: () => setOpenDate(true),
                 // Set readOnly attribute
              },
              sx: {
                '& .MuiOutlinedInput-input': {
                  backgroundColor: 'white !important'
              },
              }
            }
          }}
          readOnly={readOnly}
        />
      )}
    </Fragment>
  );
};

export default DateRange;
